import { createContext, useContext, useMemo, useEffect, useCallback } from 'react'
import { useLocation as useRouterLocation, useNavigate } from 'react-router-dom'
import { getCityBySlug, getAllCities, CITIES } from '@utils/geolocation'
import { useAuth } from './AuthContext'
import { MetaPixel } from '@services/metaPixel'
import storage from '@services/storage'


const LocationContext = createContext(null)

export const LocationProvider = ({ children }) => {
  const routerLocation = useRouterLocation()
  const navigate = useNavigate()

  // Derive city from URL pathname
  const city = useMemo(() => {
    const pathSegments = routerLocation.pathname.split('/').filter(Boolean);
    const citySlug = pathSegments[0]; // Expecting /udi or /ribeirao

    if (citySlug) {
      const foundCity = getCityBySlug(citySlug);
      // Return foundCity or null, without navigating immediately
      return foundCity;
    }
    return null; // Default to null if no city slug
  }, [routerLocation.pathname]);

  // Effect to handle redirection for invalid city slugs or non-city routes
  useEffect(() => {
    const pathSegments = routerLocation.pathname.split('/').filter(Boolean);
    const citySlugInPath = pathSegments[0];

    // Ignora se parecer um arquivo estático (extensão) para não redirecionar assets
    const isStaticFile = /\.(png|jpg|jpeg|gif|svg|ico|webp|js|css|md|json)$/i.test(routerLocation.pathname);

    if (isStaticFile) return;

    // If there is a city slug in the path but it's not valid, redirect to root
    // Exclude the dedicated /validar-email route from this redirection
    if (citySlugInPath && citySlugInPath !== 'validar-email' && !city) {
      navigate('/', { replace: true });
    }
  }, [city, routerLocation.pathname, navigate]);

  const { user } = useAuth()

  // Effect to set the theme and store selected city slug
  useEffect(() => {
    if (city?.theme) {
      document.documentElement.setAttribute('data-theme', city.theme);
    } else {
      // Set a default theme if no city is selected (e.g., when on '/')
      document.documentElement.setAttribute('data-theme', 'default'); 
    }

    if (city?.slug) {
      storage.setItem('lastSelectedCitySlug', city.slug);
    }
  }, [city]);

  // Effect to initialize and track page views globally for the selected city/unit
  useEffect(() => {
    let pixelId = city?.meta_pixel_id;
    
    // Fallback robust mapping directly in code for the two cities
    if (!pixelId && city?.slug) {
      if (city.slug === 'uberlandia') {
        pixelId = '709654841304062';
      } else if (city.slug === 'ribeirao-preto' || city.slug === 'ribeirao') {
        pixelId = '1721007521716925';
      }
    }

    if (pixelId) {
      const userData = {};
      if (user) {
        if (user.email) userData.email = user.email;
        if (user.phone) userData.phone = user.phone;
        if (user.name) userData.name = user.name;
      }
      
      MetaPixel.init(pixelId, userData);
      MetaPixel.track(pixelId, 'PageView');
    }
  }, [city, routerLocation.pathname, user]);



  const changeCity = useCallback((newCitySlug) => {
    const pathSegments = routerLocation.pathname.split('/').filter(Boolean);
    
    // If we're on a city-specific route (first segment is city slug)
    if (pathSegments.length > 0 && getCityBySlug(pathSegments[0])) {
      const pageName = pathSegments[1];
      
      // If we are on an event or checkout page, go to the new city home
      if (pageName === 'evento' || pageName === 'checkout') {
        navigate(`/${newCitySlug}`);
        return;
      }

      // Otherwise, preserve the remaining path
      const remainingPath = '/' + pathSegments.slice(1).join('/');
      navigate(`/${newCitySlug}${remainingPath}`);
    } else {
      // No city slug found, go to the new city home
      navigate(`/${newCitySlug}`);
    }
  }, [routerLocation.pathname, navigate]);

  const cities = useMemo(() => getAllCities(), []);

  const value = useMemo(() => ({
    city,
    cities,
    isUberlandia: city?.slug === CITIES.uberlandia.slug,
    isRibeirao: city?.slug === CITIES.ribeirao_preto.slug,
    changeCity,
  }), [city, cities, changeCity]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  )
}

export const useCityContext = () => {
  const context = useContext(LocationContext)
  
  if (!context) {
    throw new Error('useCityContext deve ser usado dentro de LocationProvider')
  }
  
  return context
}



